import { Box, Stack } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { LocalizedEnum } from "../../../../../modules/localization/components/AppLocalized"
import { MeetingServiceOrderWorkgroup } from "../../../../../modules/merchant/accounting/accounting/accountingTypes"
import { NullableCalendarEvent } from "../../../createOrder/components/resource/calendar/fullCalendarTypes"
import { SelectedMainUserState } from "../../../createOrder/createOrderTypes"
import { tailwindPalette } from "../../../../../modules/theme/tailwindPalette"
import { useDateTime } from "../../../../../modules/localization/useDateTime"
import {
    DeliveryTimesField,
    DeliveryFulfillmentType,
    DynamicConfigKey,
} from "../../../createOrder/components/form/formTypes"

import { formatDeliveryDateForReceipt, formattedMeetingTimeRange } from "./helpers"
import { WorkgroupInfo } from "./WorkgroupInfo"
import {
    SectionBlock,
    SectionItemData,
    SectionItemLabel,
    SectionItemLink,
    SectionSubTitle,
    SectionTitleDataContainer,
} from "./common/ReceiptPrimitives"

const useStyles = makeStyles()((theme) => ({
    container: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "1.5rem",
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "1fr",
            gap: "0",
        },
    },
    box: {
        background: tailwindPalette.blue[50],
        [theme.breakpoints.down("sm")]: {
            background: theme.palette.common.white,
            borderRadius: 0,
            padding: "2rem 1.25rem",
            borderBottom: `1px solid ${theme.palette.divider}`,
        },
    },
    darkColor: {
        color: theme.palette.text.primary,
    },
    textWrap: {
        "& > p:first-of-type": {
            color: theme.palette.text.secondary,
        },
    },
    email: {
        color: tailwindPalette.slate[500],
    },
    rightAlignData: {
        textAlign: "right",
    },
}))

type ContactPerson = Readonly<{
    name?: string
    email?: string
    phones?: ReadonlyArray<string>
}>

type DetailsProps = Readonly<{
    orderDetails: Record<string, any>
    mainUser: Partial<SelectedMainUserState> & Omit<ContactPerson, "name">
    resourceNames: string
    workgroup?: MeetingServiceOrderWorkgroup
}>

export function Details({ orderDetails, mainUser, resourceNames, workgroup }: DetailsProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const event: NullableCalendarEvent = orderDetails["event"]

    const multipleDeliveries: DeliveryTimesField | undefined = orderDetails[DynamicConfigKey.MULTIPLE_DELIVERIES]
    const deliveryDateValue = multipleDeliveries?.deliveryDate ?? null
    const firstDeliveryTimeValue = multipleDeliveries?.deliveryTimes?.[0]?.time ?? null
    const formattedDate = formatDeliveryDateForReceipt(dateTimeFactory, {
        deliveryDateValue,
        firstDeliveryTimeValue,
    })

    const formattedMeetingTime = formattedMeetingTimeRange(dateTimeFactory, event?.start, event?.end)

    const deliveryType: DeliveryFulfillmentType = orderDetails[DynamicConfigKey.DELIVERY_TYPE]
    const organizers: ContactPerson | undefined = orderDetails[DynamicConfigKey.MEETING_RESPONSIBLE]

    const createdValue = orderDetails["created"]
    const createdDateTime = createdValue ? dateTimeFactory.fromApi(createdValue) : null
    const createdTime =
        createdDateTime && createdDateTime.isValid ? dateTimeFactory.formatDateTime(createdDateTime) : null

    const updatedValue = orderDetails["updated"]
    const updatedDateTime = updatedValue ? dateTimeFactory.fromApi(updatedValue) : null
    const updatedTime =
        updatedDateTime && updatedDateTime.isValid ? dateTimeFactory.formatDateTime(updatedDateTime) : null

    const meetingNameValue = orderDetails[DynamicConfigKey.MEETING_NAME]
    const otherDeliveryLocationValue = orderDetails[DynamicConfigKey.OTHER_DELIVERY_LOCATION]
    const numberOfSeatsValue = orderDetails[DynamicConfigKey.NUMBER_OF_SEATS]
    const merchantName = orderDetails["priceList"]?.merchantName

    return (
        <Box className={classes.container}>
            <SectionBlock
                classNames={classes.box}
                translationKey="confirm-order-dialog-meeting-details"
                title="Meeting details"
            >
                <Stack gap=".5rem">
                    {meetingNameValue && <SectionSubTitle>{meetingNameValue}</SectionSubTitle>}
                    {formattedMeetingTime && (
                        <SectionTitleDataContainer>
                            <SectionItemLabel translationKey="confirm-order-dialog-meeting-time">
                                Meeting time:
                            </SectionItemLabel>
                            <SectionItemData classNames={classes.rightAlignData}>
                                {formattedMeetingTime}
                            </SectionItemData>
                        </SectionTitleDataContainer>
                    )}
                    {formattedDate && (
                        <SectionTitleDataContainer>
                            <SectionItemLabel translationKey="confirm-order-dialog-delivery-date">
                                Delivery date:
                            </SectionItemLabel>
                            <SectionItemData classNames={classes.rightAlignData}>{formattedDate}</SectionItemData>
                        </SectionTitleDataContainer>
                    )}
                    {deliveryType && (
                        <SectionTitleDataContainer>
                            <SectionItemLabel translationKey="confirm-order-dialog-delivery-location">
                                Delivery location:
                            </SectionItemLabel>
                            <SectionItemData classNames={classes.rightAlignData}>
                                {deliveryType === DeliveryFulfillmentType.DELIVERY ? (
                                    !!otherDeliveryLocationValue ? (
                                        otherDeliveryLocationValue
                                    ) : (
                                        resourceNames
                                    )
                                ) : (
                                    <LocalizedEnum
                                        base="confirm-order-dialog-delivery-location-mode"
                                        enumValue={deliveryType}
                                    >
                                        <>{deliveryType}</>
                                    </LocalizedEnum>
                                )}
                            </SectionItemData>
                        </SectionTitleDataContainer>
                    )}
                    {!!numberOfSeatsValue && (
                        <SectionTitleDataContainer>
                            <SectionItemLabel translationKey="confirm-order-dialog-seats">Seats:</SectionItemLabel>
                            <SectionItemData>{numberOfSeatsValue}</SectionItemData>
                        </SectionTitleDataContainer>
                    )}
                    {merchantName && (
                        <SectionTitleDataContainer>
                            <SectionItemLabel translationKey="confirm-order-dialog-merchant">
                                Merchant:
                            </SectionItemLabel>
                            <SectionItemData>{merchantName}</SectionItemData>
                        </SectionTitleDataContainer>
                    )}
                    {createdTime && (
                        <SectionTitleDataContainer>
                            <SectionItemLabel translationKey="confirm-order-timestamp-created">
                                Created:
                            </SectionItemLabel>
                            <SectionItemData>{createdTime}</SectionItemData>
                        </SectionTitleDataContainer>
                    )}
                    {updatedTime && (
                        <SectionTitleDataContainer>
                            <SectionItemLabel translationKey="confirm-order-timestamp-updated">
                                Updated:
                            </SectionItemLabel>
                            <SectionItemData>{updatedTime}</SectionItemData>
                        </SectionTitleDataContainer>
                    )}
                    {workgroup && (
                        <SectionTitleDataContainer>
                            <SectionItemLabel translationKey="confirm-order-dialog-workgroup">
                                Workgroup:
                            </SectionItemLabel>
                            <WorkgroupInfo workgroup={workgroup} />
                        </SectionTitleDataContainer>
                    )}
                </Stack>
            </SectionBlock>

            <SectionBlock classNames={classes.box} translationKey="confirm-order-dialog-contact" title="Contact">
                <Stack gap={1}>
                    {(organizers?.name || organizers?.email) && (
                        <SectionTitleDataContainer classNames={classes.rightAlignData}>
                            <SectionItemLabel translationKey="confirm-order-dialog-host">Host:</SectionItemLabel>
                            <Box>
                                <SectionItemData>{organizers.name}</SectionItemData>
                                {organizers?.email && (
                                    <SectionItemLink href={`mailto:${organizers.email}`}>
                                        {organizers.email}
                                    </SectionItemLink>
                                )}
                                {organizers?.phones?.map((phone) => (
                                    <SectionItemData key={phone}>{phone}</SectionItemData>
                                ))}
                            </Box>
                        </SectionTitleDataContainer>
                    )}
                    <SectionTitleDataContainer classNames={classes.rightAlignData}>
                        <SectionItemLabel translationKey="confirm-order-dialog-ordered-by">
                            Ordered by:
                        </SectionItemLabel>
                        <Box>
                            <SectionItemData>{mainUser.displayName}</SectionItemData>
                            {mainUser?.email && (
                                <SectionItemLink href={`mailto:${mainUser.email}`}>{mainUser.email}</SectionItemLink>
                            )}
                            {mainUser?.phones?.map((phone) => (
                                <SectionItemData key={phone}>{phone}</SectionItemData>
                            ))}
                        </Box>
                    </SectionTitleDataContainer>
                </Stack>
            </SectionBlock>
        </Box>
    )
}
